import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { welcomeApi } from '../api/welcome';
import { WelcomeModal } from './WelcomePage/WelcomeModal';
import type { WelcomePage } from '../types';

export function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [welcomePage, setWelcomePage] = useState<WelcomePage | null>(null);
  const navigate = useNavigate();

  const { login, isLoading, error, clearError } = useAuthStore();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    clearError();

    if (!username || !password) {
      return;
    }

    try {
      const loggedInUser = await login({ username, password });

      // Check if user needs to see updated welcome page
      try {
        const welcome = await welcomeApi.getCurrent();
        const lastSeen = loggedInUser.preferences.lastSeenWelcomeVersion || 0;

        if (welcome.version > lastSeen) {
          // Show welcome modal
          setWelcomePage(welcome);
          setShowWelcomeModal(true);
        } else {
          // Navigate directly to home
          navigate('/');
        }
      } catch (err) {
        // If welcome page fails to load, just navigate to home
        console.error('Failed to load welcome page:', err);
        navigate('/');
      }
    } catch (err) {
      // Error is already set in the store
    }
  };

  const handleCloseWelcome = () => {
    setShowWelcomeModal(false);
    navigate('/');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-monster-dark px-4">
      <div className="w-full max-w-md">
        {/* Logo/Title */}
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold mb-2 bg-gradient-to-r from-monster-blue to-monster-crimson bg-clip-text text-transparent">
            Monstervision
          </h1>
          <p className="text-gray-400 text-lg">Song Contest 2025</p>
        </div>

        {/* Login Card */}
        <div className="card">
          <h2 className="text-2xl font-bold mb-6 text-center">Welcome Back</h2>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-monster-crimson/10 border border-monster-crimson rounded text-monster-crimson text-sm">
              {error}
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="username" className="block text-sm font-medium mb-2">
                Username
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={isLoading}
                required
                autoFocus
                className="w-full"
                placeholder="Enter your username"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                required
                className="w-full"
                placeholder="Enter your password"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading || !username || !password}
              className="w-full btn-primary mt-6"
            >
              {isLoading ? 'Logging in...' : 'Log In'}
            </button>
          </form>
        </div>

        {/* Footer */}
        <div className="text-center text-gray-500 text-sm mt-8">
          <p>A Eurovision-style competition for monsters</p>
          <p className="mt-2 text-xs opacity-50">v1.3.0-downloads</p>
        </div>
      </div>

      {/* Welcome Modal (shown if version is new) */}
      {showWelcomeModal && welcomePage && (
        <WelcomeModal welcomePage={welcomePage} onClose={handleCloseWelcome} />
      )}
    </div>
  );
}
